import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as MarketActions from 'app/main/marketplace/store/actions';
import * as ActionsDialog from 'app/store/actions';

export default function useMarketplace({ provider, form, setField, propsMarketId }) {
    const dispatch = useDispatch();
    const marketplaceData = useSelector(({ marketStack }) => marketStack.marketStack.list);
    const selectedMarketStack = useSelector(({ marketStack }) => marketStack.marketStack.stack);
    const globalProvisionPreview = useSelector(({ globalProvision }) => globalProvision?.preview);

    const marketId = selectedMarketStack?.id || propsMarketId;
    const isMarketStack = Boolean(selectedMarketStack);
    const currentMarketStack = marketplaceData?.find(item => item.id === marketId) || selectedMarketStack;

    const title = currentMarketStack
        ? `${currentMarketStack.name} ${currentMarketStack.version}`
        : form?.hostname || 'New Cluster';
    const imageSrc = currentMarketStack?.logo
        ? `assets/images/logos/${currentMarketStack.logo}`
        : `assets/images/providers/${provider}.png`;

    const config = currentMarketStack?.config || null;

    // Clean up marketplace state when form unmounts
    useEffect(() => {
        return () => dispatch(MarketActions.clearStack());
    }, [dispatch]);

    // Manager override (marketplace data may load after form init)
    useEffect(() => {
        if (isMarketStack && config?.resource_manager && form && form.manager !== config.resource_manager) {
            setField('manager', config.resource_manager);
        }
    }, [isMarketStack, config?.resource_manager, form?.manager, setField]);

    // Auto-exclude all global scripts when marketplace config requires it
    useEffect(() => {
        if (config?.disable_global_scripts && globalProvisionPreview?.configs?.length > 0) {
            const allScriptIds = [];
            globalProvisionPreview.configs.forEach(c => {
                if (c.scripts) c.scripts.forEach(s => allScriptIds.push(`${c._id}-${s._id}`));
            });
            if (allScriptIds.length > 0) setField('excludedGlobalScripts', allScriptIds);
        }
    }, [config?.disable_global_scripts, globalProvisionPreview?.configs, setField]);

    // Download provision script from marketplace URL (if configured)
    useEffect(() => {
        if (!config?.provision_script || !form || form.provisionScript) return;
        let cancelled = false;
        (async () => {
            try {
                dispatch(ActionsDialog.toggleLoading());
                const scriptContent = await dispatch(MarketActions.getProvisionScript(config.provision_script));
                if (!cancelled && scriptContent) setField('provisionScript', scriptContent);
            } catch (err) {
                console.error('Error downloading provision script:', err);
            } finally {
                dispatch(ActionsDialog.toggleLoading());
            }
        })();
        return () => { cancelled = true; };
    }, [config?.provision_script, form?.provisionScript, dispatch, setField]);

    // Pre-populate applications from marketplace config
    useEffect(() => {
        if (isMarketStack && config?.applications?.length > 0 && !(form?.applications?.length > 0)) {
            setField('applications', config.applications.map((app, i) => ({
                id: app.id || `${currentMarketStack.id || 'app'}-${i}`,
                name: app.name || '',
                url: app.url || '',
                icon: app.icon || 'apps',
                description: app.description || '',
                category: app.category || 'general',
                launchType: app.launchType || 'url',
                instructions: app.instructions || '',
                enabled: app.enabled !== false,
                actionType: app.actionType || 'access-tab',
                actionButtonLabel: app.actionButtonLabel || '',
            })));
        }
    }, [isMarketStack, config?.applications, form?.applications, setField, currentMarketStack?.id]);

    return {
        marketId,
        isMarketStack,
        title,
        imageSrc,
        config,
        disableStackFields: isMarketStack,
        allowedOsTypes: config?.os_types || null,
        allowedComputeIds: config?.computeNodeSize || null,
        hideProvisionScript: config?.hide_provision_script === true,
        disableGlobalScripts: config?.disable_global_scripts === true,
    };
}
